import { useLingui } from "@lingui/react/macro";
import type { Me, ThinkingLevel } from "@rakazo/contracts";
import {
  CLOUDFLARE_AI_GATEWAY_PROVIDER_ID,
  cloudflareGatewayRouting,
  DEFAULT_MODEL_CONTEXT_WINDOW,
  DEFAULT_MODEL_MAX_TOKENS,
  MAX_MODEL_CONTEXT_WINDOW,
  MAX_MODEL_MAX_TOKENS,
  OPENAI_COMPATIBLE_PROVIDER_ID,
  openAiCompatibleConnectReady,
  openAiCompatibleProbeSuccessMessage,
  parseModelContextWindow,
  parseModelMaxImagesPerPrompt,
  parseModelMaxTokens,
} from "@rakazo/contracts";
import {
  COMPATIBLE_THINKING_LEVELS,
  clampCatalogThinkingLevel,
  compatibleModelRows,
  createModelProbe,
  initialModelProbeState,
  pickCatalogModelId,
  popularModelProvidersFirst,
  savedCompatibleModelIds,
} from "@rakazo/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useCopyText } from "../../lib/copy-text";
import type { ModelCatalogEntry, ModelCredential } from "../../lib/model-auth";
import { thinkingLevelLabel } from "../../lib/model-catalog";
import type { ModelPreflightFailure } from "../../lib/model-connection-preflight";
import {
  classifyModelConnectionFailure,
  loadStoredModelAuth,
  modelPreflightSuccessMessage,
  openAiCompatibleTestOutcome,
  runModelConnectionPreflight,
  unavailableSelectedModel,
} from "../../lib/model-connection-preflight";
import { useSpaceRpc } from "../../lib/space-rpc";
import { useModelOAuthSignIn } from "../../lib/use-model-oauth-signin";
import { errorText } from "../../lib/user-error";

function connectionMaxTokensField(providerId: string, stored: number | undefined): string {
  if (providerId === OPENAI_COMPATIBLE_PROVIDER_ID) {
    return String(stored ?? DEFAULT_MODEL_MAX_TOKENS);
  }
  return stored !== undefined ? String(stored) : "";
}

type ModelDraft = {
  reasoning: boolean;
  maxTokens: string;
  contextWindow: string;
  maxImagesPerPrompt: string;
};

/** Each enabled model's saved settings, as the form edits them. */
function credentialModelDrafts(
  credential: ModelCredential | undefined,
): Record<string, ModelDraft> {
  return Object.fromEntries(
    Object.entries(credential?.modelSettings ?? {}).map(([id, settings]) => [
      id,
      {
        reasoning: settings.reasoning,
        maxTokens: String(settings.maxTokens ?? DEFAULT_MODEL_MAX_TOKENS),
        contextWindow: String(settings.contextWindow ?? DEFAULT_MODEL_CONTEXT_WINDOW),
        maxImagesPerPrompt: String(settings.maxImagesPerPrompt ?? ""),
      },
    ]),
  );
}

// Connections saved before the per-model list carry one flag, for their default model.
function credentialVisionModelIds(credential: ModelCredential | undefined): string[] {
  if (credential?.visionModelIds) return [...credential.visionModelIds];
  return credential?.supportsImages && credential.modelId ? [credential.modelId] : [];
}

export function useModelSettings(onClose: () => void) {
  const rpc = useSpaceRpc();
  const { t } = useLingui();
  const [catalog, setCatalog] = useState<ModelCatalogEntry[]>([]);
  const [credentials, setCredentials] = useState<ModelCredential[]>([]);
  const [me, setMe] = useState<Me | null>(null);
  const [provider, setProvider] = useState("");
  // The provider whose optional personal-key form is open.
  const [ownKeyProvider, setOwnKeyProvider] = useState<string | null>(null);
  const [providerQuery, setProviderQuery] = useState("");
  const [modelId, setModelId] = useState("");
  const modelIdRef = useRef(modelId);
  modelIdRef.current = modelId;
  const [enabledModelIds, setEnabledModelIds] = useState<string[]>([]);
  const [apiKey, setApiKey] = useState("");
  const [accountId, setAccountId] = useState("");
  const [gatewayId, setGatewayId] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [reasoning, setReasoning] = useState(false);
  const [thinkingLevel, setThinkingLevel] = useState<ThinkingLevel | null>(null);
  const [maxTokens, setMaxTokens] = useState(String(DEFAULT_MODEL_MAX_TOKENS));
  const [contextWindow, setContextWindow] = useState(String(DEFAULT_MODEL_CONTEXT_WINDOW));
  // Openai-compatible: which models read images, set per model.
  const [visionModelIds, setVisionModelIds] = useState<string[]>([]);
  const [maxImagesPerPrompt, setMaxImagesPerPrompt] = useState("");
  // Openai-compatible: the fields above edit settingsModelId; the other models' edits wait here.
  const [settingsModelId, setSettingsModelId] = useState("");
  const [modelDrafts, setModelDrafts] = useState<Record<string, ModelDraft>>({});
  const [{ models: probeModels, probing }, setProbe] = useState(initialModelProbeState);
  const [modelProbe] = useState(() => createModelProbe(setProbe));
  const resetOpenAiCompatibleProbe = modelProbe.reset;
  const [codeCopied, copyOAuthCode] = useCopyText();
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<"connect" | "settings" | "default" | "disconnect" | null>(
    null,
  );
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preflightTesting, setPreflightTesting] = useState(false);
  const [preflightTarget, setPreflightTarget] = useState<
    "api-key" | "sign-in" | "compatible" | null
  >(null);
  const [preflightSuccess, setPreflightSuccess] = useState<string | null>(null);
  const [preflightFailure, setPreflightFailure] = useState<ModelPreflightFailure | null>(null);
  const preflightRevisionRef = useRef(0);
  const pageScrollRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const refreshRevisionRef = useRef(0);
  const selectionRevisionRef = useRef(0);
  const selectedLabelRef = useRef<string | undefined>(undefined);

  const {
    oauth,
    pasteCode,
    setPasteCode,
    oauthPending,
    popupBlocked,
    cancelOAuthAttempt,
    startSubscriptionSignIn,
    submitOAuthCode,
  } = useModelOAuthSignIn({
    onClearError: () => setError(null),
    onError: setError,
    onFinished: async () => {
      await refresh();
    },
  });

  async function refresh() {
    const refreshRevision = ++refreshRevisionRef.current;
    const selectionRevision = selectionRevisionRef.current;
    const [nextCatalog, nextCredentials, nextMe] = await Promise.all([
      rpc.models.list(),
      rpc.models.credentials(),
      rpc.me(),
    ]);
    if (refreshRevision !== refreshRevisionRef.current) return;
    const nextProvider =
      provider && nextCatalog.some((entry) => entry.provider === provider)
        ? provider
        : (nextMe.defaultProvider ?? nextCatalog[0]?.provider ?? "");
    const nextCredential = nextCredentials.find((entry) => entry.provider === nextProvider);
    const nextModel =
      nextProvider === OPENAI_COMPATIBLE_PROVIDER_ID
        ? (nextCredential?.modelId ??
          (nextMe.defaultProvider === OPENAI_COMPATIBLE_PROVIDER_ID ? nextMe.defaultModel : "") ??
          "")
        : (nextCatalog.find((entry) => entry.provider === nextProvider && entry.id === modelId)
            ?.id ??
          pickCatalogModelId(
            nextCatalog,
            nextProvider,
            nextCredential?.modelId ?? nextMe.defaultModel,
          ));
    setCatalog(nextCatalog);
    setCredentials(nextCredentials);
    setMe(nextMe);
    if (selectionRevision === selectionRevisionRef.current) {
      invalidatePreflight();
      resetOpenAiCompatibleProbe();
      setProvider(nextProvider);
      setModelId(nextModel);
      setEnabledModelIds(nextCredential?.enabledModelIds ?? []);
      if (nextProvider === OPENAI_COMPATIBLE_PROVIDER_ID) {
        setBaseUrl(nextCredential?.baseUrl ?? "");
        setReasoning(nextCredential?.reasoning ?? false);
        setThinkingLevel(
          clampCatalogThinkingLevel(
            nextCredential?.modelId === nextModel ? nextCredential?.thinkingLevel : null,
            nextCredential?.reasoning ? COMPATIBLE_THINKING_LEVELS : [],
          ) as ThinkingLevel | null,
        );
        setContextWindow(String(nextCredential?.contextWindow ?? DEFAULT_MODEL_CONTEXT_WINDOW));
        setVisionModelIds(credentialVisionModelIds(nextCredential));
        setMaxImagesPerPrompt(String(nextCredential?.maxImagesPerPrompt ?? ""));
        setModelDrafts(credentialModelDrafts(nextCredential));
        setSettingsModelId(nextModel);
      } else {
        // A credential's stored effort is bound to its saved model choice.
        const nextEntry = nextCatalog.find(
          (entry) => entry.provider === nextProvider && entry.id === nextModel,
        );
        setThinkingLevel(
          clampCatalogThinkingLevel(
            nextCredential?.modelId === nextModel ? nextCredential.thinkingLevel : null,
            nextEntry?.thinkingLevels,
          ) as ThinkingLevel | null,
        );
      }
      setMaxTokens(connectionMaxTokensField(nextProvider, nextCredential?.maxTokens));
      setAccountId(nextCredential?.accountId ?? "");
      setGatewayId(nextCredential?.gatewayId ?? "");
    }
  }

  useEffect(() => {
    void refresh()
      .catch((err: unknown) => setError(errorText(err, t`Could not load model settings`)))
      .finally(() => setLoading(false));
    return () => {
      refreshRevisionRef.current += 1;
      modelProbe.invalidate();
    };
  }, []);

  const groups = useMemo(() => {
    const grouped = new Map<string, ModelCatalogEntry[]>();
    for (const entry of catalog) {
      const entries = grouped.get(entry.provider) ?? [];
      entries.push(entry);
      grouped.set(entry.provider, entries);
    }
    return popularModelProvidersFirst(
      [...grouped].map(([id, entries]) => ({
        id,
        name: entries[0]?.providerName ?? id,
        entries,
      })),
      (group) => group.id,
    );
  }, [catalog]);
  const credentialByProvider = useMemo(
    () => new Map(credentials.map((entry) => [entry.provider, entry])),
    [credentials],
  );
  const connectedProviderIds = useMemo(
    () => new Set(credentials.map((entry) => entry.provider)),
    [credentials],
  );
  const searching = providerQuery.trim() !== "";
  const filteredGroups = useMemo(() => {
    const query = providerQuery.trim().toLowerCase();
    const matched = query
      ? groups.filter((group) =>
          [group.id, group.name, ...group.entries.flatMap((entry) => [entry.id, entry.label])]
            .join(" ")
            .toLowerCase()
            .includes(query),
        )
      : groups;
    // Rank exact/prefix provider-name hits above incidental substring matches,
    // then float connected providers so they are reachable without scrolling.
    const score = (group: (typeof groups)[number]) =>
      query
        ? group.id.toLowerCase().startsWith(query) || group.name.toLowerCase().startsWith(query)
          ? 0
          : group.name.toLowerCase().includes(query) || group.id.toLowerCase().includes(query)
            ? 1
            : 2
        : 0;
    return [...matched].sort(
      (a, b) =>
        score(a) - score(b) ||
        Number(connectedProviderIds.has(b.id)) - Number(connectedProviderIds.has(a.id)),
    );
  }, [groups, providerQuery, connectedProviderIds]);
  // Browsing separates connected providers into their own section; searching
  // flattens back into one ranked list.
  const connectedGroups = useMemo(
    () => (searching ? [] : filteredGroups.filter((group) => connectedProviderIds.has(group.id))),
    [searching, filteredGroups, connectedProviderIds],
  );
  const otherGroups = useMemo(
    () =>
      searching
        ? filteredGroups
        : filteredGroups.filter((group) => !connectedProviderIds.has(group.id)),
    [searching, filteredGroups, connectedProviderIds],
  );
  const modelsForProvider = catalog.filter((entry) => entry.provider === provider);
  const selected = modelsForProvider.find((entry) => entry.id === modelId) ?? modelsForProvider[0];
  selectedLabelRef.current = selected?.label;
  const selectedProviderName = selected?.providerName ?? selected?.provider ?? "";
  const disconnectName = selectedProviderName;
  const hostCredentialSource = me?.hostCredentialSource ?? "";
  const isOpenAiCompatible = provider === OPENAI_COMPATIBLE_PROVIDER_ID;
  const isCloudflareGateway = provider === CLOUDFLARE_AI_GATEWAY_PROVIDER_ID;
  const cloudflareRoutingReady =
    !isCloudflareGateway || cloudflareGatewayRouting({ accountId, gatewayId }) !== undefined;
  const credential = credentials.find((entry) => entry.provider === provider);
  const currentEntry = catalog.find(
    (entry) => entry.provider === me?.defaultProvider && entry.id === me?.defaultModel,
  );
  const activeCredential = credentials.find(
    (entry) => entry.provider === me?.defaultProvider && entry.modelId === me?.defaultModel,
  );
  // The banner shows the effective effort — stored level or the runtime
  // default — only when the active model can actually think.
  const activeThinkingLabel =
    (currentEntry?.thinkingLevels ?? []).some((level) => level !== "off") ||
    activeCredential?.reasoning
      ? thinkingLevelLabel(activeCredential?.thinkingLevel ?? "medium")
      : null;
  const isActive =
    me?.defaultProvider === selected?.provider &&
    me?.defaultModel === (isOpenAiCompatible ? modelId.trim() : selected?.id);
  // Space still bills the host while this provider matches; model id alone is not credentials.
  const usingServerCredentials = provider === me?.hostCredentialProvider;
  const acceptsKey = selected?.auth !== "oauth";
  const subscriptionSignIn = selected?.signIn !== undefined;
  // Effort levels for the staged catalog model — "off" stays out, matching the
  // per-bot Thinking picker.
  const catalogThinkingLevels =
    !isOpenAiCompatible && selected
      ? (selected.thinkingLevels ?? []).filter((level) => level !== "off")
      : [];
  const selectedStoredLevel =
    !isOpenAiCompatible && credential?.modelId === selected?.id
      ? (credential?.thinkingLevel ?? null)
      : null;
  const thinkingDirty = !isOpenAiCompatible && (thinkingLevel ?? null) !== selectedStoredLevel;
  const busy = pending !== null || oauthPending;
  const effectiveBaseUrl = baseUrl.trim();
  const openAiCompatibleReady = openAiCompatibleConnectReady({
    baseUrl: effectiveBaseUrl,
    modelId,
  });
  const compatibleRows = compatibleModelRows(
    savedCompatibleModelIds(credential, baseUrl),
    enabledModelIds,
    probeModels,
  );
  const defaultModelOptions = [...new Set([...enabledModelIds, modelId])];
  // Saved-connection settings edited without a new key (output limit, gateway ids).
  const settingsDirty =
    !isOpenAiCompatible &&
    Boolean(credential) &&
    (maxTokens.trim() !== connectionMaxTokensField(provider, credential?.maxTokens) ||
      (isCloudflareGateway &&
        (accountId.trim() !== (credential?.accountId ?? "") ||
          gatewayId.trim() !== (credential?.gatewayId ?? ""))));

  // The page scrolls as one column, so the top of the connection details can sit above the
  // view after a long form; scroll back up to it, and never move a page that already shows it.
  function revealDetailTop() {
    const page = pageScrollRef.current;
    const detail = detailRef.current;
    if (!page || !detail) return;
    const offset = detail.getBoundingClientRect().top - page.getBoundingClientRect().top;
    if (offset < 0) page.scrollTo({ top: page.scrollTop + offset });
  }

  function invalidatePreflight() {
    preflightRevisionRef.current += 1;
    setPreflightTesting(false);
    setPreflightTarget(null);
    setPreflightSuccess(null);
    setPreflightFailure(null);
  }

  function preflightStillCurrent(revision: number): boolean {
    return revision === preflightRevisionRef.current;
  }

  function updateBaseUrl(nextBaseUrl: string) {
    setBaseUrl(nextBaseUrl);
    // A new endpoint starts with no ticks; the saved endpoint gets its saved list back.
    setEnabledModelIds(savedCompatibleModelIds(credential, nextBaseUrl));
    resetOpenAiCompatibleProbe();
    invalidatePreflight();
    setError(null);
    setNotice(null);
  }

  function updateApiKey(nextApiKey: string) {
    setApiKey(nextApiKey);
    resetOpenAiCompatibleProbe();
    invalidatePreflight();
  }

  // The Advanced fields speak for one model at a time: the one in the Model field, unless
  // another enabled model is picked to edit.
  const editedModelId = (settingsModelId || modelId).trim();
  const supportsImages = visionModelIds.includes(editedModelId);
  function currentDraft(): ModelDraft {
    return { reasoning, maxTokens, contextWindow, maxImagesPerPrompt };
  }
  function allModelDrafts(): Record<string, ModelDraft> {
    return editedModelId ? { ...modelDrafts, [editedModelId]: currentDraft() } : modelDrafts;
  }
  function editSettingsFor(nextModelId: string): ModelDraft {
    const all = allModelDrafts();
    const next = all[nextModelId.trim()] ?? currentDraft();
    setModelDrafts(all);
    setSettingsModelId(nextModelId.trim());
    setReasoning(next.reasoning);
    setMaxTokens(next.maxTokens);
    setContextWindow(next.contextWindow);
    setMaxImagesPerPrompt(next.maxImagesPerPrompt);
    return next;
  }
  function setModelImages(id: string, value: boolean) {
    setVisionModelIds((current) =>
      value
        ? current.includes(id)
          ? current
          : [...current, id]
        : current.filter((entry) => entry !== id),
    );
  }
  function setSupportsImages(value: boolean) {
    setModelImages(editedModelId, value);
  }

  function stageCompatibleModelId(nextModelId: string) {
    const next = editSettingsFor(nextModelId);
    setModelId(nextModelId);
    setThinkingLevel(
      clampCatalogThinkingLevel(
        credential?.modelId === nextModelId ? credential.thinkingLevel : null,
        next.reasoning ? COMPATIBLE_THINKING_LEVELS : [],
      ) as ThinkingLevel | null,
    );
  }

  function chooseProvider(nextProvider: string) {
    cancelOAuthAttempt();
    selectionRevisionRef.current += 1;
    const nextCredential = credentials.find((entry) => entry.provider === nextProvider);
    const nextModelId =
      nextProvider === OPENAI_COMPATIBLE_PROVIDER_ID
        ? (nextCredential?.modelId ?? "")
        : pickCatalogModelId(catalog, nextProvider, nextCredential?.modelId ?? me?.defaultModel);
    setProvider(nextProvider);
    setReasoning(nextCredential?.reasoning ?? false);
    const nextEntry = catalog.find(
      (entry) => entry.provider === nextProvider && entry.id === nextModelId,
    );
    setThinkingLevel(
      clampCatalogThinkingLevel(
        nextCredential?.modelId === nextModelId ? nextCredential.thinkingLevel : null,
        nextProvider === OPENAI_COMPATIBLE_PROVIDER_ID
          ? nextCredential?.reasoning
            ? COMPATIBLE_THINKING_LEVELS
            : []
          : nextEntry?.thinkingLevels,
      ) as ThinkingLevel | null,
    );
    setMaxTokens(connectionMaxTokensField(nextProvider, nextCredential?.maxTokens));
    setContextWindow(String(nextCredential?.contextWindow ?? DEFAULT_MODEL_CONTEXT_WINDOW));
    setVisionModelIds(credentialVisionModelIds(nextCredential));
    setMaxImagesPerPrompt(String(nextCredential?.maxImagesPerPrompt ?? ""));
    setModelDrafts(credentialModelDrafts(nextCredential));
    setSettingsModelId(nextModelId);
    setModelId(nextModelId);
    setEnabledModelIds(nextCredential?.enabledModelIds ?? []);
    setBaseUrl(
      nextProvider === OPENAI_COMPATIBLE_PROVIDER_ID ? (nextCredential?.baseUrl ?? "") : "",
    );
    revealDetailTop();
    setApiKey("");
    setAccountId(nextCredential?.accountId ?? "");
    setGatewayId(nextCredential?.gatewayId ?? "");
    resetOpenAiCompatibleProbe();
    invalidatePreflight();
    setError(null);
    setNotice(null);
  }

  async function probeServerModels() {
    if (!baseUrl.trim()) return;
    const revision = preflightRevisionRef.current;
    setError(null);
    setNotice(null);
    setPreflightSuccess(null);
    setPreflightFailure(null);
    setPreflightTarget("compatible");
    await modelProbe.probe({
      baseUrl,
      apiKey,
      request: rpc.models.probeOpenAiCompatible,
      onSuccess: (models) => {
        if (!preflightStillCurrent(revision)) return;
        const current = modelIdRef.current;
        const next = current.trim() || models[0] || "";
        if (next !== current) stageCompatibleModelId(next);
        else setModelId(next);
        const unavailable = unavailableSelectedModel(current, models);
        if (unavailable) {
          setPreflightFailure(unavailable);
          setNotice(null);
          return;
        }
        setPreflightTarget(null);
        setPreflightFailure(null);
        setNotice(openAiCompatibleProbeSuccessMessage(models.length));
      },
      onError: (err) => {
        if (!preflightStillCurrent(revision)) return;
        setPreflightFailure(classifyModelConnectionFailure(err, { modelId }));
        setNotice(null);
      },
    });
  }

  async function testCompatibleConnection() {
    const testedModelId = modelId.trim();
    if (!effectiveBaseUrl || !testedModelId) return;
    const revision = preflightRevisionRef.current;
    setPreflightTarget("compatible");
    setPreflightTesting(true);
    setPreflightSuccess(null);
    setPreflightFailure(null);
    setError(null);
    setNotice(null);
    try {
      const { status } = await rpc.models.testOpenAiCompatible({
        baseUrl: effectiveBaseUrl,
        modelId: testedModelId,
        apiKey,
      });
      if (!preflightStillCurrent(revision)) return;
      const outcome = openAiCompatibleTestOutcome(status);
      setPreflightSuccess(outcome.success);
      setPreflightFailure(outcome.failure);
    } catch (err) {
      if (!preflightStillCurrent(revision)) return;
      setPreflightFailure(classifyModelConnectionFailure(err, { modelId: testedModelId }));
    }
    setPreflightTesting(false);
  }

  async function testApiKeyConnection() {
    if (!selected?.catalogProbe) return;
    const revision = preflightRevisionRef.current;
    setPreflightTarget("api-key");
    setPreflightTesting(true);
    setPreflightSuccess(null);
    setPreflightFailure(null);
    setError(null);
    setNotice(null);
    const result = await runModelConnectionPreflight({
      authKind: "api-key",
      provider: selected.provider,
      apiKey,
      modelId: selected.id,
      catalogProbe: true,
      probeCatalog: rpc.models.probeCatalog,
    });
    if (!preflightStillCurrent(revision)) return;
    setPreflightTesting(false);
    if (result.ok) {
      setPreflightSuccess(modelPreflightSuccessMessage(result.discoveredModels.length));
      return;
    }
    setPreflightFailure(result.failure);
  }

  async function testOAuthConnection() {
    if (!selected) return;
    const revision = preflightRevisionRef.current;
    setPreflightTarget("sign-in");
    setPreflightTesting(true);
    setPreflightSuccess(null);
    setPreflightFailure(null);
    setError(null);
    setNotice(null);
    const stored = await loadStoredModelAuth(
      selected.provider,
      () => rpc.models.credentials(),
      selected.id,
    );
    if (!preflightStillCurrent(revision)) return;
    const result = await runModelConnectionPreflight({
      authKind: "oauth",
      provider: selected.provider,
      ...stored,
    });
    if (!preflightStillCurrent(revision)) return;
    setPreflightTesting(false);
    if (result.ok) {
      setPreflightSuccess(t`A subscription credential is stored securely.`);
      return;
    }
    setPreflightFailure(result.failure);
  }

  async function setModelDefault() {
    if (!selected || !credential) return;
    const activeModelId = isOpenAiCompatible ? modelId.trim() : selected.id;
    if (isOpenAiCompatible && !activeModelId) return;
    setError(null);
    setNotice(null);
    setPending("default");
    try {
      await rpc.models.setDefault({
        provider: selected.provider,
        modelId: activeModelId,
        // Catalog connections keep the space default effort on the preference;
        // openai-compatible still owns its level inside the stored endpoint config.
        ...(!isOpenAiCompatible
          ? {
              thinkingLevel: clampCatalogThinkingLevel(
                thinkingLevel,
                selected.thinkingLevels,
              ) as ThinkingLevel | null,
            }
          : {}),
      });
      await refresh();

      setNotice(isOpenAiCompatible ? t`Model updated.` : t`Now using ${selected.label}.`);
    } catch (err) {
      setError(errorText(err, t`Could not change the default model`));
    } finally {
      setPending(null);
    }
  }

  // settingsOnly saves a connected catalog provider's limits and gateway ids
  // without touching the key field; key replacement stays on its own button.
  async function connectKey({ settingsOnly = false }: { settingsOnly?: boolean } = {}) {
    if (!selected) return;
    const newKey = settingsOnly ? "" : apiKey.trim();
    const savingLimitOnly = !isOpenAiCompatible && !newKey;
    const activeModelId = isOpenAiCompatible ? modelId.trim() : selected.id;
    // The connection-wide values saved are the default model's own, whichever model the fields
    // show; every edited model's settings go along with them.
    const drafts = allModelDrafts();
    const { reasoning, maxTokens, contextWindow, maxImagesPerPrompt } = isOpenAiCompatible
      ? (drafts[activeModelId] ?? currentDraft())
      : currentDraft();
    const supportsImages = visionModelIds.includes(activeModelId);
    const modelSettings: Record<
      string,
      {
        reasoning: boolean;
        maxTokens: number;
        contextWindow: number;
        maxImagesPerPrompt: number | null;
      }
    > = {};
    if (isOpenAiCompatible) {
      for (const [id, draft] of Object.entries(drafts)) {
        if (id !== activeModelId && !enabledModelIds.includes(id)) continue;
        const images = visionModelIds.includes(id);
        const draftMaxTokens = parseModelMaxTokens(draft.maxTokens);
        const draftContextWindow = parseModelContextWindow(draft.contextWindow);
        const draftImages = parseModelMaxImagesPerPrompt(draft.maxImagesPerPrompt, images);
        if (
          draftMaxTokens === undefined ||
          draftContextWindow === undefined ||
          draftMaxTokens >= draftContextWindow ||
          (images && draft.maxImagesPerPrompt.trim() && draftImages === undefined)
        ) {
          editSettingsFor(id);
          setError(t`Check the settings of ${id}.`);
          return;
        }
        modelSettings[id] = {
          reasoning: draft.reasoning,
          maxTokens: draftMaxTokens,
          contextWindow: draftContextWindow,
          maxImagesPerPrompt:
            images && draft.maxImagesPerPrompt.trim() ? (draftImages ?? null) : null,
        };
      }
    }
    const supportedThinking = isOpenAiCompatible
      ? reasoning
        ? COMPATIBLE_THINKING_LEVELS
        : []
      : selected.thinkingLevels;
    // The staged effort belongs to the model on screen. Clamp it before connect
    // or a limit save so a previous model's level cannot stick.
    const stagedThinking = clampCatalogThinkingLevel(
      thinkingLevel,
      supportedThinking,
    ) as ThinkingLevel | null;
    const modelChanged = (credential?.modelId ?? null) !== (activeModelId || null);
    if (isOpenAiCompatible) {
      if (!effectiveBaseUrl || !modelId.trim()) return;
    } else if (savingLimitOnly) {
      if (!credential) return;
    } else if (newKey.length < 8) {
      return;
    }
    const parsedMaxTokens = maxTokens.trim() ? parseModelMaxTokens(maxTokens) : undefined;
    if ((isOpenAiCompatible || maxTokens.trim()) && parsedMaxTokens === undefined) {
      setError(
        t`Enter a whole number from 1 to ${MAX_MODEL_MAX_TOKENS} for maximum output tokens.`,
      );
      return;
    }
    const parsedMaxImagesPerPrompt = isOpenAiCompatible
      ? parseModelMaxImagesPerPrompt(maxImagesPerPrompt, supportsImages)
      : undefined;
    if (
      isOpenAiCompatible &&
      supportsImages &&
      maxImagesPerPrompt.trim() &&
      parsedMaxImagesPerPrompt === undefined
    ) {
      setError(t`Enter a whole number from 1 to 1000 for the image limit.`);
      return;
    }
    const maxImagesPerPromptInput =
      supportsImages && !maxImagesPerPrompt.trim() ? null : parsedMaxImagesPerPrompt;
    const parsedContextWindow = isOpenAiCompatible
      ? parseModelContextWindow(contextWindow)
      : undefined;
    if (isOpenAiCompatible && parsedContextWindow === undefined) {
      setError(
        t`Enter a whole number from 1 to ${MAX_MODEL_CONTEXT_WINDOW} for the context limit.`,
      );
      return;
    }
    if (isOpenAiCompatible && parsedMaxTokens === undefined) return;
    // Connecting or editing another provider leaves the space default alone; Use as default
    // moves it. The first connection of a space becomes its default.
    const makeDefault =
      Boolean(credential?.isDefault) || !credentials.some((entry) => entry.isDefault);
    setError(null);
    setNotice(null);
    setPending(settingsOnly ? "settings" : "connect");
    try {
      await rpc.models.connect(
        isOpenAiCompatible
          ? {
              provider: selected.provider,

              baseUrl: effectiveBaseUrl,
              modelId: modelId.trim(),
              // The server adds the default itself.
              enabledModelIds: enabledModelIds.filter((id) => id !== modelId.trim()),
              reasoning,
              thinkingLevel: stagedThinking,
              maxTokens: parsedMaxTokens,
              contextWindow: parsedContextWindow,
              supportsImages,
              visionModelIds: visionModelIds.filter(
                (id) => id === modelId.trim() || enabledModelIds.includes(id),
              ),
              maxImagesPerPrompt: maxImagesPerPromptInput,
              modelSettings,
              apiKey: apiKey.trim() || undefined,
              label: selected.providerName ?? selected.provider,
              makeDefault,
            }
          : {
              provider: selected.provider,

              ...(newKey ? { apiKey: newKey } : {}),
              ...(isCloudflareGateway
                ? { accountId: accountId.trim(), gatewayId: gatewayId.trim() }
                : {}),
              modelId: selected.id,
              // A limits-only save leaves the stored effort alone while the model
              // stays put. Changing the model sends the clamped level, including
              // null, so the previous model's effort is not reused.
              ...(!savingLimitOnly || modelChanged || thinkingDirty
                ? { thinkingLevel: stagedThinking }
                : {}),
              maxTokens: parsedMaxTokens ?? null,
              label: selected.providerName ?? selected.provider,
              makeDefault,
            },
      );
      if (!settingsOnly) setApiKey("");
      await refresh();
      revealDetailTop();
      // A fresh connection speaks for itself through the Active model row.

      if (isOpenAiCompatible || savingLimitOnly || !makeDefault) setNotice(t`Saved.`);
    } catch (err) {
      setError(errorText(err, t`Could not connect this provider`));
    } finally {
      setPending(null);
    }
  }

  async function disconnectCredential() {
    if (!selected || !credential) return;
    cancelOAuthAttempt();
    setError(null);
    setNotice(null);
    setPending("disconnect");
    try {
      await rpc.models.disconnect({ provider: selected.provider });
      setApiKey("");
      setThinkingLevel(null);
      await refresh();
      revealDetailTop();
      setNotice(t`Disconnected ${selected.providerName ?? selected.provider}.`);
    } catch (err) {
      setError(errorText(err, t`Could not disconnect this provider`));
    } finally {
      setPending(null);
    }
  }

  function handleClose() {
    cancelOAuthAttempt(false);
    onClose();
  }

  function beginSelectedSubscriptionSignIn() {
    if (!selected) return;
    setNotice(null);
    void startSubscriptionSignIn({
      provider: selected.provider,
      modelId: selected.id,
      thinkingLevel: clampCatalogThinkingLevel(
        thinkingLevel,
        selected.thinkingLevels,
      ) as ThinkingLevel | null,
      label: selected.providerName ?? selected.provider,
    });
  }

  return {
    acceptsKey,
    accountId,
    activeThinkingLabel,
    apiKey,
    baseUrl,
    beginSelectedSubscriptionSignIn,
    busy,
    cancelOAuthAttempt,
    catalog,
    catalogThinkingLevels,
    chooseProvider,
    cloudflareRoutingReady,
    codeCopied,
    compatibleRows,
    confirmDisconnect,
    connectKey,
    connectedGroups,
    contextWindow,
    copyOAuthCode,
    credential,
    credentialByProvider,
    credentials,
    currentEntry,
    defaultModelOptions,
    detailRef,
    disconnectCredential,
    disconnectName,
    effectiveBaseUrl,
    enabledModelIds,
    error,
    filteredGroups,
    gatewayId,
    groups,
    handleClose,
    hostCredentialSource,
    invalidatePreflight,
    isActive,
    isCloudflareGateway,
    isOpenAiCompatible,
    loading,
    maxImagesPerPrompt,
    maxTokens,
    me,
    modelId,
    modelsForProvider,
    notice,
    oauth,
    oauthPending,
    openAiCompatibleReady,
    otherGroups,
    ownKeyProvider,
    pageScrollRef,
    pasteCode,
    pending,
    popupBlocked,
    preflightFailure,
    preflightSuccess,
    preflightTarget,
    preflightTesting,
    probeModels,
    probeServerModels,
    probing,
    provider,
    providerQuery,
    reasoning,
    selected,
    selectedProviderName,
    selectionRevisionRef,
    setAccountId,
    setConfirmDisconnect,
    setContextWindow,
    setEnabledModelIds,
    setError,
    setGatewayId,
    setMaxImagesPerPrompt,
    setMaxTokens,
    setModelDefault,
    setModelId,
    setModelImages,
    editSettingsFor,
    settingsModelId: editedModelId,
    setNotice,
    setOwnKeyProvider,
    setPasteCode,
    setProviderQuery,
    setReasoning,
    setSupportsImages,
    setThinkingLevel,
    settingsDirty,
    stageCompatibleModelId,
    submitOAuthCode,
    subscriptionSignIn,
    supportsImages,
    testApiKeyConnection,
    testCompatibleConnection,
    testOAuthConnection,
    visionModelIds,
    thinkingDirty,
    thinkingLevel,
    updateApiKey,
    updateBaseUrl,
    usingServerCredentials,
  };
}
export type ModelSettingsState = ReturnType<typeof useModelSettings>;
